'use client';

import { useState } from 'react';
import { Box, Grid2, useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useComputedColor } from '@/hooks/useComputedColor';
import { useCorporateStore } from '@/infrastructure/zustand/corporateStoreProvider';
import { Text } from '@/ui/Text';
import { Modal } from '@/ui/modal/Modal';
import { useLanguageStore } from '@/infrastructure/zustand/languageStoreProvider';
import type { CampaignConfig } from '../config.interface';
import { transformToLangCode } from '@/infrastructure/zustand/languageStore';
import { languages, type Language } from '@/shared';

export const ResumeShortImageContainer = ({
	campaignId,
	campaignImage,
	title,
	config,
}: {
	campaignId: string;
	campaignImage: string;
	title: string;
	config: CampaignConfig;
}) => {
	const themeBaseColor = useCorporateStore((state) => state.metadata.themeBaseColor);
	const textColor = useComputedColor(undefined, themeBaseColor);
	const theme = useTheme();
	const isTablet = useMediaQuery(theme.breakpoints.up('sm'));
	const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
	const isNextHubMax = useMediaQuery('(min-width:1779px)');
	const currentLangCode = useLanguageStore((state) => state.currentLangCode);

	const storeMerchantLogo = useCorporateStore((state) => state.merchant.logo);
	const merchantLogo =
		typeof config.merchantLogo === 'string'
			? (config.merchantLogo ?? storeMerchantLogo ?? '')
			: (config.merchantLogo?.(currentLangCode) ?? storeMerchantLogo ?? '');
	const logoHeight = config.logoHeight ?? 40;

	const textMarginTop =
		isTablet && !isDesktop && !isNextHubMax
			? '14rem'
			: isDesktop && !isNextHubMax
				? '8.5rem'
				: isNextHubMax
					? '20rem'
					: undefined;

	const isAreasPricelessPlanetCampaign = campaignId === '1c0269da-2061-48fe-8f54-52d92f802306';

	const sxcampaignInfoVideoContainer = {
		position: 'relative',
		[theme.breakpoints.down('sm')]: {},
	};

	const sxngoLogo = {
		zIndex: 2,
		position: 'absolute',
		top: '3rem',
		left: '50%',
		transform: 'translateX(-50%)',
	} as const;

	return (
		<>
			{!config.showAlalaWebm ? (
				<Grid2
					size={{ xs: 12 }}
					style={{ height: '60vh' }}
					sx={config.isAlala ? sxcampaignInfoVideoContainer : undefined}
				>
					<div
						style={{
							position: 'relative',
							width: '100%',
							backgroundRepeat: 'no-repeat',
							backgroundPosition: 'center',
							backgroundSize: 'cover',
							height: '100%',
							padding: '3% 5%',
							backgroundImage: `url(${campaignImage})`,
						}}
					>
						{!config.disableMerchantLogo && (
							// eslint-disable-next-line @next/next/no-img-element
							<img
								alt="merchantLogo"
								src={merchantLogo}
								height={logoHeight}
								style={{
									...(config.isAlala ? sxngoLogo : {}),
									// Show logos with a better size like the one on fundacionrealmadrid.worldcoo.com
									width: '100%',
									maxWidth: 100,
									objectFit: 'cover',
									objectPosition: 'left top',
									overflow: 'unset',
								}}
							/>
						)}
						{!config.disableTitle && (
							<div
								style={{
									backgroundColor: themeBaseColor.trim(),
									position: 'absolute',
									left: '5%',
									width: '40%',
									bottom: '5%',
									textAlign: 'left',
								}}
							>
								{isAreasPricelessPlanetCampaign ? (
									<AreasPricelessPlanetDisclaimer text={title} textColor={textColor} />
								) : (
									<Text text={title} style={{ padding: '0px 20px', color: textColor, fontWeight: 900 }} />
								)}
							</div>
						)}
						{config.bottomLogo && (
							// eslint-disable-next-line @next/next/no-img-element
							<img
								src={config.bottomLogo}
								alt="logo"
								style={{
									marginTop: '20px',
									position: 'absolute',
									maxHeight: '14%',
									width: '100%',
									bottom: 0,
									left: 0,
									backgroundColor: 'white',
								}}
							/>
						)}
					</div>
				</Grid2>
			) : (
				<Grid2 size={{ xs: 12 }} sx={sxcampaignInfoVideoContainer}>
					<video
						autoPlay
						muted
						loop
						style={{
							width: '100%',
							right: '0',
							bottom: '0',
							left: '0',
							top: '0',
						}}
					>
						<source src={'/video-gg.webm'} type="video/webm" />
					</video>
					{!config.disableMerchantLogo && (
						// eslint-disable-next-line @next/next/no-img-element
						<img alt="merchantLogo" style={sxngoLogo} src={merchantLogo} height={logoHeight} />
					)}
					{isTablet && (
						<Text
							text={title}
							style={{
								position: 'absolute',
								backgroundColor: '#ED77BF',
								width: '12rem',
								color: textColor,
								fontWeight: 900,
								top: textMarginTop,
								marginTop: isNextHubMax ? '3rem' : undefined,
								padding: '0px 20px',
								zIndex: 2,
							}}
						/>
					)}
				</Grid2>
			)}
		</>
	);
};

// AREAS CUSTOM COMPONENT

const areasDisclaimer = {
	[languages.Catalan]: {
		title: `<strong>Declaració de responsabilitat Donació de la Coalició Priceless Planet a Areas</strong>`,
		body: `<p>Les donacions realitzades a través d'AREAS en favor del projecte de Priceless Planet Coalition no són deduïbles d'impostos segons la legislació espanyola. Mastercard, Conservación Internacional Areas i Worldcoo no garanteixen, ja sigui explícitament o implícitament, l'exempció de l'organització a l'impost sobre la renda o la seva elegibilitat per rebre contribucions deduïbles d'impostos. La verificació de l'elegibilitat de qualsevol organització benèfica per rebre contribucions deduïbles d'impostos i la determinació de la mesura que la seva(es) contribució(és) és(són) deduïble(s) és totalment responsabilitat seva, el donant. Us recomanem encaridament que, com a donant, consulteu un advocat, comptable i/o assessor financer abans de realitzar qualsevol donació. Qualsevol donació realitzada a través del Servei es fa sota la pròpia responsabilitat.</p>`,
	},
	[languages.Spanish]: {
		title: `<strong>Declaración de responsabilidad Donación de la Coalición Priceless Planet a Areas</strong>`,
		body: `<p>Las donaciones realizadas a través de AREAS en favor del proyecto de Priceless Planet Coalition no son deducibles de impuestos según la legislación española.  Mastercard, Conservación Internacional Áreas y Worldcoo no garantizan, ya sea explícita o implícitamente, la exención de la organización al impuesto sobre la renta o su elegibilidad para recibir contribuciones deducibles de impuestos. La verificación de la elegibilidad de cualquier organización benéfica para recibir contribuciones deducibles de impuestos y la determinación de la medida en que su(s) contribución(es) es(son) deducible(s) es totalmente responsabilidad suya, el donante. Le recomendamos encarecidamente que, como donante, consulte a un abogado, contable y/o asesor financiero antes de realizar cualquier donación. Cualquier donación realizada a través del Servicio se hace bajo su propia responsabilidad.</p>`,
	},
	[languages.English]: {
		title: `<strong>Disclaimer Priceless Planet Coalition donation at Areas</strong>`,
		body: `<p>Donations to Priceless Planet Coalition carried out through AREAS are not tax-deductible under Spanish law.  Mastercard, Conservation International, Areas and Worldcoo do not guarantee, whether explicit or implied, the exempt status of the organization to income tax or their eligibility to receive tax-deductible contributions. The verification of any charitable organization’s eligibility to receive tax-deductible contributions and the determination of the extent to which your contribution(s) is deductible is fully the responsibility of you, the donor. We highly advise that you, as a donor, consult an attorney, accountant, and/or financial advisor before making any donation. Any donation made on the Service is done so at your own risk.</p>`,
	},
} satisfies Partial<Record<Language, { title: string; body: string }>>;

const areasLearnMore: Record<string, string> = {
	[languages.Spanish]: `<p><strong>Las donaciones realizadas para esta campaña no serán deducibles de impuestos <span style="text-decoration: underline">(leer más)</span>. </strong></p>`,
	[languages.Catalan]: `<p><strong>Les donacions realitzades per aquesta campanya no seran deduïbles d'impostos <span style="text-decoration: underline">(llegir més)</span>. </strong></p>`,
	[languages.English]: `<p><strong>Donations made for this campaign will not be tax deductible <span style="text-decoration: underline">(learn more)</span>.</strong></p>`,
};

const AreasPricelessPlanetDisclaimer = (props: { text: string; textColor: string }) => {
	const [modalVisible, setModalVisible] = useState(false);
	const { i18n } = useTranslation();

	const getDiscalimer = (key: 'title' | 'body') => {
		const langCode = transformToLangCode(i18n.language);
		const texts =
			langCode && langCode in areasDisclaimer ? areasDisclaimer[langCode as keyof typeof areasDisclaimer] : undefined;
		if (texts) {
			return texts[key];
		}

		return areasDisclaimer[languages.English][key];
	};

	const getLearnMore = () => {
		const text = areasLearnMore[transformToLangCode(i18n.language)!];
		if (text) {
			return text;
		}

		return areasLearnMore[languages.English];
	};

	return (
		<>
			<Box onClick={() => setModalVisible(true)} sx={{ '&:hover': { cursor: 'pointer' } }}>
				<Text text={getLearnMore()} style={{ padding: '0px 20px', color: props.textColor, fontWeight: 900 }} />
			</Box>
			<Modal open={modalVisible} handleClose={() => setModalVisible(false)}>
				<Text text={getDiscalimer('title')} />
				<Text text={getDiscalimer('body')} />
			</Modal>
		</>
	);
};
